import React, { useState } from 'react';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  EmailOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../axios';

const EnhancedAttractiveLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async () => {
    if (!email || !password) {
      setError('All fields are required!');
      return;
    }
    try {
      const response = await httpClient.post('/auth/login', {
        email,
        password,
      });
      console.log('Login Successful:', response.data);

      // Store the token in localStorage or context
      localStorage.setItem('token', response.data.token);
      console.log('Token stored:', localStorage.getItem('token')); // Verify storage
      if (response.data.roleName.toLowerCase() == 'employee') {
        setTimeout(() => {
          navigate('/emptask', {
            state: {
              userId: response.data.userId,
              userName: response.data.userName,
            },
          });
        }, 100);
      } else if (response.data.roleName.toLowerCase() == 'admin') {
        setTimeout(() => {
          navigate('/admin');
        }, 100);
      }
    } catch (err) {
      console.error(
        'Login Error:',
        err.response?.data || err.message
      );
      setError(err.response?.data?.message || 'Invalid credentials!');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background:
          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          width: '120%',
          height: '120%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          transform: 'rotate(45deg)',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          right: '-10%',
          width: '120%',
          height: '120%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          transform: 'rotate(-45deg)',
          zIndex: 1,
        }}
      />

      <Container
        maxWidth='xs'
        sx={{ position: 'relative', zIndex: 2 }}
      >
        <Paper
          elevation={24}
          sx={{
            borderRadius: 5,
            padding: 4,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 30px 60px rgba(0,0,0,0.2)',
            border: '1px solid rgba(255,255,255,0.3)',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-10px)',
              boxShadow: '0 40px 70px rgba(0,0,0,0.25)',
            },
          }}
        >
          {/* Animated Gradient Border */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 5,
              background: 'linear-gradient(90deg, #667eea, #764ba2)',
              backgroundSize: '200% 100%',
              animation: 'gradient-flow 3s ease infinite',
              '@keyframes gradient-flow': {
                '0%': { backgroundPosition: '100% 0' },
                '100%': { backgroundPosition: '-100% 0' },
              },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                background:
                  'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
                boxShadow: '0 15px 30px rgba(102,126,234,0.4)',
                transform: 'perspective(500px) rotateX(10deg)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform:
                    'perspective(500px) rotateX(0deg) scale(1.05)',
                },
              }}
            >
              <LockOutlined sx={{ color: 'white', fontSize: 60 }} />
            </Box>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
                background:
                  'linear-gradient(45deg, #667eea, #764ba2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Secure Login
            </Typography>
            {error && (
              <Typography
                color='error'
                sx={{
                  textAlign: 'center',
                  mb: 2,
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            label='Email Address'
            variant='outlined'
            margin='normal'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <EmailOutlined color='action' />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label='Password'
            type={showPassword ? 'text' : 'password'}
            variant='outlined'
            margin='normal'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#764ba2',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <LockOutlined color='action' />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge='end'
                  >
                    {showPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant='contained'
            fullWidth
            onClick={handleLogin}
            sx={{
              mt: 2,
              py: 1.5,
              background:
                'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
              transition: 'all 0.3s ease',
              fontWeight: 600,
              letterSpacing: 1,
              '&:hover': {
                transform: 'scale(1.03)',
                boxShadow: '0 15px 30px rgba(102,126,234,0.4)',
              },
            }}
          >
            Sign In
          </Button>

          <Typography
            variant='body2'
            sx={{
              mt: 2,
              textAlign: 'center',
              color: '#7f8c8d',
            }}
          >
            Don't have an account?
            <Button
              color='primary'
              size='small'
              sx={{
                ml: 1,
                color: '#667eea',
                fontWeight: 600,
                '&:hover': {
                  background: 'rgba(102,126,234,0.1)',
                },
              }}
              onClick={() => navigate('/register')}
            >
              Sign Up
            </Button>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default EnhancedAttractiveLogin;
